/* eslint-disable camelcase */
import { mapGetters } from 'vuex'
import { validateDataEntryModels } from '@/utils/helpers/Validate'
import { isJeBalanced } from '@/utils/helpers/DataEntryViews/IsTransactionBalanced'
import { flattenArrayRecursive } from '@/utils/helpers/FlattenArray'
import { createAuditReport, createPostingReport, getPostingReport, createBillPdf } from '@/utils/helpers/DataEntryViews/CreateReport'
import { notify } from '@/utils/helpers/Notify'
import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'
import { LineSaver } from '@/utils/helpers/DataEntryViews/LineSaver'
import { payload as sourcecodeActionPayload } from '@/components/RouteComponents/Sourcecodes/actionPayload'
import { getSimpleDate } from '@/utils/helpers/FormatDate'
import pageSizes from '@/utils/pageSizes'
import moment from 'moment'

export const transactionEditViewMixins = {
  data () {
    return {
      fetchingPoData: false,
      headerDeltas: {},
      lineDeltas: {},
      hasBeenChanged: false,
      altRoute: null,
      linesHaveBeenChanged: false,
      distLinesHeight: 400,
      fileCount: 0,
      saver: null,
      dcConfirmationDialogVisible: false,
      allDcs: [],
      newDcs: [],
      existingDcs: [],
      jesToVoid: {},
      creatingDc: false,
      viewedDcId: null,
      lineFieldsToClear: [],
      dcSettings: {},
      vendorPoLines: [],
      voidingDcJes: false,
      dcJeUpdateErrMsg: null,
      isPcReimbursementTransaction: false,
      isDCUpdated: false,
      unAllowedStatusesToChange: ['POSTED', 'CLOSED', 'VOIDED']
    }
  },
  methods: {
    /**
     *
     * @returns Whether User should be able to update the payment date or not based on settings
     */
    allowPaymentDateUpdate () {
      return this.$store.getters.getUiSettings['ALLOW_PAYMENT_DATE_UPDATES']
    },
    /**
     * Handles the formatting of distribution lines to account for DCs.
     * @param incoming
     * @param je
     * @return {[]}
     */
    buildLinesWithDcs (incoming, je = null) {
      let lines = []
      let lineNumber = 0
      let userSourcecode = {}
      if (je && je.source_code && parseInt(je.source_code) > 0) {
        userSourcecode = this.$store.getters.getSourcecodeById(je.source_code)
      }
      let offsetJEStatus = this.$store.getters.getOffsetJEStatusById(this.$route.params.id) || false
      if (offsetJEStatus) {
        incoming.sort((a, b) => a.line_number - b.line_number).forEach(line => {
          let newLines = []
          if (line.offset_by && userSourcecode.code !== 'DC') { // Could be one line or many depending on whether line was split
            let offsetLines = this.mapOffsetLineValuesToLine(line).map((updated, index) => { // so we need to return an array
              lineNumber++
              return { line_number: lineNumber, form: { ...updated } }
            })
            if (offsetLines.length > 1) {
              offsetLines = offsetLines.map(offsetLine => {
                offsetLine.isSplittingLine = true
                offsetLine.splits = line.id
                return offsetLine
              })
            }
            newLines = offsetLines
          } else {
            lineNumber++
            newLines = [({ line_number: lineNumber, form: { ...line } })]
          }
          lines = Object.freeze([...lines, ...newLines])
        })
      } else {
        const sortedLines = incoming
        .sort((a, b) => a.line_number - b.line_number)
        .map(line => ({
          line_number: line.line_number,
          form: { ...line }
        }))
        lines = Object.freeze([...lines, ...sortedLines])
      }
      return lines
    },
    /**
     * Handles inserting lines in the parent transaction component when a line is split.
     * @param lineNumber
     * @param numberOfLinesToInsert
     */
    handleLineInsertion ({ lineNumber, numberOfLinesToInsert }) {
      this.originalLines = [...this.originalLines] // Trigger reactivity
      let i = 1
      while (i <= numberOfLinesToInsert) {
        const newLine = this.lines.find(line => line.line_number === lineNumber + i)
        this.originalLines.splice(parseInt(lineNumber), 0, { ...newLine })
        i++
      }
      this.originalLines = this.originalLines.map((line, index) => {
        line.line_number = index + 1
        return line
      })
    },
    /**
     * Find original PO lines with updated remaining balance and close/reopen information based on the current state of the transaction
     * @return {array}
     */
    buildOriginalPoLines (lines) {
      const safeFixed = (val, digits = 2) => {
        const n = parseFloat(val)
        return Number.isFinite(n) ? n.toFixed(digits) : (0).toFixed(digits)
      }

      const cloneForm = (item) => Object.assign({}, item.form || item)
      const lineById = {}
      lines.forEach(line => {
        const form = cloneForm(line)
        lineById[Number(form.id)] = { line, form }
      })

      const parentLines = lines.filter(l => {
        const f = cloneForm(l)
        return !f.referencing_line
      })

      const openChildLines = lines.filter(l => {
        const f = cloneForm(l)
        return f.referencing_line && f.type === 'OPEN'
      })

      const closedChildLines = lines.filter(l => {
        const f = cloneForm(l)
        return f.referencing_line && f.type === 'CLOSED' && f.source_type === 'Manual'
      })

      const originalPoLines = parentLines.map(parent => {
        const form = cloneForm(parent)
        form.remaining_decimal = ['OPEN'].includes(form.type) ? form.amount_decimal : '0.00'
        form.close_reopen_id = form.id

        let firstOpenChildId = null
        let firstClosedChildId = null
        let remainingBalance = 0

        // Iterate through open child lines to find ones that reference this parent line, and calculate remaining balance and close/reopen id based on those child lines
        openChildLines.forEach(child => {
          const childForm = cloneForm(child)
          let currentRef = Number(childForm.referencing_line)
          let root = null

          while (currentRef && lineById[currentRef]) {
            const referenced = lineById[currentRef].form
            if (!referenced.referencing_line) {
              root = referenced
              break
            }
            currentRef = Number(referenced.referencing_line)
          }

          if (root && Number(root.id) === Number(form.id)) {
            remainingBalance += parseFloat(childForm.amount_decimal || 0)
            if (!firstOpenChildId) {
              firstOpenChildId = Number(childForm.id)
            }
          }
        })

        // If there is no open child line referencing this parent line, iterate through closed child lines to find ones that reference this parent line, and calculate close/reopen id based on those child lines
        if (!firstOpenChildId) {
          closedChildLines.forEach(child => {
            const childForm = cloneForm(child)
            let currentRef = Number(childForm.referencing_line)
            let root = null

            while (currentRef && lineById[currentRef]) {
              const referenced = lineById[currentRef].form
              if (!referenced.referencing_line) {
                root = referenced
                break
              }
              currentRef = Number(referenced.referencing_line)
            }

            if (root && Number(root.id) === Number(form.id)) {
              if (!firstClosedChildId) {
                firstClosedChildId = Number(childForm.id)
              }
            }
          })
        }

        if (firstOpenChildId) {
          form.remaining_decimal = safeFixed(remainingBalance)
          form.close_reopen_id = firstOpenChildId
          form.type = 'OPEN'
        }

        if (firstClosedChildId) {
          form.remaining_decimal = safeFixed(0)
          form.close_reopen_id = firstClosedChildId
          form.source_type = 'Manual'
          form.type = 'CLOSED'
        }

        const relieved = Number(form.amount_decimal || 0) - Number(form.remaining_decimal || 0)
        form.relieved_amount_decimal = safeFixed(relieved)

        const outLine = parent.form ? Object.assign({}, parent, { form }) : form
        return outLine
      })

      return originalPoLines
    },
    /**
     * Sets this.viewedDcId to the id of the offset line passed in lineId. This allows the user to view a DC on a
     * line-by-line basis.
     * @param lineId
     */
    viewDc (lineId = null) {
      this.viewedDcId = lineId
    },
    /**
     * Accepts a date string and returns it formatted according to the user's locale, determined by browser language
     * @param date
     * @return {string}
     */
    formatDateToLocale (date) {
      const locale = window.navigator.userLanguage || window.navigator.language || 'en-US'
      const m = moment(date).locale(locale)
      const localeFormat = m.localeData() && typeof m.localeData().longDateFormat === 'function' && m.localeData().longDateFormat('L')
        ? m.localeData().longDateFormat('L').replace(/\//g, '-')
        : 'YYYY-MM-DD'
      return m.format(localeFormat)
    },
    /**
     * Populates the fields under the Vendor or Batch header inputs that display information
     * such as Type, Company, Production, Bank, etc., when a new Batch or Vendor is created
     * from this view (using the + buttons in the Vendor or Batch header fields).
     * @param modelReferenceObject
     */
    populateFieldWithNewModel (modelReferenceObject) {
      const { type, getter, id } = modelReferenceObject
      this.form[type].value = id
      this.form[type].label = type === 'vendor' ? this.$store.getters[getter](id).code + ' - ' + this.$store.getters[getter](id).name : this.$store.getters[getter](id).name
      this.updateForm(this.form)
      if (type === 'batch') {
        this.setBatchAttributeLineValues(this.batchAttributes)
      }
    },
    /**
     * Sets certain line properties to the value of the corresponding property on an offset line
     * @param original
     * @return {object}
     */
    mapOffsetLineValuesToLine (original) {
      const { getLineById, getJeLinesByTransactionId } = this.$store.getters
      const offset = getLineById('Je', original.offset_by) // Getting the offset JE line with ID that matches the original line
      if (offset && offset.id) {
        const dcJeLines = getJeLinesByTransactionId(offset.transaction_id) // Get the DC JE lines that correspond to the transaction id of the offset line
        const overridingLines = Object.values(dcJeLines).filter(line => { // Find the line that overrides this line
          const meta = line.meta_data
          if (meta && typeof offset.meta_data === 'object') {
            const originalMeta = offset.meta_data.original_line
            const originalLineType = offset.meta_data.original_line_type
            return (meta.original_line && meta.original_line === originalMeta && meta.original_line_type === originalLineType && !meta.hasOwnProperty('offset_attempt'))
          }
          return false
        }).sort((a, b) => a.line_number - b.line_number)
        if (overridingLines && overridingLines.length > 0 && overridingLines.every(line => line.id)) {
          return overridingLines.map(overridingLine => {
            return {
              id: overridingLine.id,
              original_line_id: original.id,
              line_number: original.line_number,
              account: overridingLine.account,
              account_code: overridingLine.account_code,
              account_name: overridingLine.account_name,
              relieves: original.relieves,
              description: overridingLine.description,
              other_name: overridingLine.other_name,
              is_offset: overridingLine.is_offset,
              amount_decimal: overridingLine.amount_decimal,
              set: overridingLine.set,
              series: overridingLine.series,
              location: overridingLine.location,
              tax_code: overridingLine.tax_code,
              free_field_1: overridingLine.free_field_1,
              free_field_2: overridingLine.free_field_2,
              free_field_3: overridingLine.free_field_3,
              free_field_4: overridingLine.free_field_4,
              asset: overridingLine.asset,
              third_party_vendor: overridingLine.third_party_vendor,
              insurance: original.insurance,
              transaction_id: overridingLine.transaction_id,
              offset_by: original.offset_by,
              meta_type: original.meta_type,
              meta_data: overridingLine.meta_data,
              recon: original.recon
            }
          })
        }
        return [original]
      }
      return [original]
    },
    /**
     * Fetches or creates a distribution change source code
     * @return {Promise<*>}
     */
    async fetchOrCreateDcSourcecode () {
      try {
        let scPayload = Object.assign({}, sourcecodeActionPayload, { query: { code: 'DC', active: 'True' } })
        let sourcecode = await this.$store.dispatch('fetchWithQuery', scPayload)
        if (!(sourcecode && sourcecode.count)) {
          const data = {
            code: 'DC',
            name: 'Distribution Change',
            description: '',
            active: true
          }
          scPayload = Object.assign({}, sourcecodeActionPayload, { data })
          sourcecode = await this.$store.dispatch('save', scPayload)
        }
        return this.$store.getters.getAllSourcecodes.find(v => v.code === 'DC')
      } catch (e) {
        console.log(e)
        return e
      }
    },
    /**
     * Populates this.allDcs, this.newDcs, and this.existingDcs and shows a confirmation dialog when saving after a DC is made
     * @param visible
     */
    showDcConfirmationDialog (visible) {
      if (visible) {
        const dcSourcecode = this.getAllSourcecodes.find(sc => sc.code === 'DC')
        this.originalLines.forEach(origLine => {
          const idType = origLine.hasOwnProperty('original_line_id') ? 'original_line_id' : 'id'
          const newLine = this.lines.find(newLine => newLine.form[idType] === origLine.form[idType])
          if (this.lineDeltas.hasOwnProperty(origLine.form.id)) {
            this.allDcs.push(newLine)
          }
          if (this.lineDeltas.hasOwnProperty(origLine.form.id) && origLine.form.offset_by && origLine.form.transaction_id) {
            const je = this.getJeById(parseInt(origLine.form.transaction_id)) // Get the JE transaction itself
            if (je && dcSourcecode && je.source_code === dcSourcecode.id) { // Check to make sure this is a DC
              this.existingDcs.push(newLine)
              this.jesToVoid[je.id] = true
            }
          } else if (this.lineDeltas.hasOwnProperty(origLine.form.id) && !origLine.form.offset_by) {
            this.newDcs.push(newLine)
          }
          if (newLine.splitBy && Array.isArray(newLine.splitBy)) {
            const splittingLines = this.lineDeltas.new.filter(sl => newLine.splitBy.includes(sl.line_number)) // TODO: this is O(n^3). Find another way.
            const ret = [...this.allDcs, ...splittingLines]
            this.allDcs = [...new Set(ret.map(dc => dc.line_number))]
              .map(lineNumber => ret.find(dc => dc.line_number === lineNumber))
            this.newDcs = [...this.newDcs, ...splittingLines]
          }
        })
      } else {
        this.allDcs = []
        this.newDcs = []
        this.existingDcs = []
        this.jesToVoid = {}
      }
      this.dcConfirmationDialogVisible = visible
    },
    async updateTransaction (linesToPreserve) {
      await this.saveTransactionChanges(this.transactionType.name, null, null, true, linesToPreserve)
      return
    },
    checkDcSettingsAllowed () {
      const keyToLabelMap = {
        account: 'Account',
        series: 'Series',
        location: 'Location',
        set: 'Set',
        description: 'Description',
        amount_decimal: 'Amount',
        free_field_1: 'FF1',
        free_field_2: 'FF2',
        free_field_3: 'FF3',
        free_field_4: 'FF4',
        tax_code: 'Tax',
        asset: 'Asset',
        third_party_vendor: '3rd'
      }

      const changedKeysSet = new Set()
      this.allDcs.forEach(dcLine => {
        const original = this.originalLines.find(
          line => line.form.id === dcLine.form.id
        )

        if (original && dcLine.form && original.form) {
          const dcForm = JSON.parse(JSON.stringify(dcLine.form))
          const originalForm = JSON.parse(JSON.stringify(original.form))

          Object.keys(keyToLabelMap).forEach(key => {
            let dcValue = dcForm[key]
            let originalValue = originalForm[key]
            if (typeof dcValue === 'object' && dcValue !== null && 'value' in dcValue) {
              dcValue = dcValue.value || ''
            }
            dcValue = dcValue || ''
            originalValue = originalValue || ''

            if (typeof dcValue === 'boolean') dcValue = dcValue ? 'true' : 'false'
            if (typeof originalValue === 'boolean') originalValue = originalValue ? 'true' : 'false'

            if (String(dcValue) !== String(originalValue)) {
              changedKeysSet.add(key)
            }
          })
        }
      })

      const changedLabels = [...changedKeysSet]
        .map(key => keyToLabelMap[key])
        .filter(Boolean)

      const onlyNonSignificant = changedLabels.every(label => this.dcSettings[label] && (this.dcSettings[label] === 'N'))

      return onlyNonSignificant
    },

    /**
     * Handles confirming DC. Checks first to see if there are existing DCs for the lines which have been changed.
     * If so, calls method to void those DC JEs. Then creates a new DC JE. Once new DC JE is created, refreshes the cached
     * JE DCs available for this transaction, so that the data is up to date if the user makes subsequent DCs.
     * @return {Promise<void>}
     */
    async confirmDcSubmit () {
      try {
        this.creatingDc = true
        const linesToPreserve = this.lines // Grab the changes made to this.lines
        const deltas = { ...this.lineDeltas }
        if (Object.keys(this.headerDeltas).length) {
          await this.submitHeaderChanges(capitalizeFirstLetter(this.transactionType.name))
        }
        if (!this.lines.some((l) => l.splitBy) && (!this.originalLines.some(line => line && line.form && line.form.offset_by) && this.checkDcSettingsAllowed())) {
          this.updateTransaction(linesToPreserve)
          this.showDcConfirmationDialog(false)
          return
        }
        if (this.existingDcs.length) {
          await this.$store.dispatch('fetchSourcecodesWithQuery')
          const voidingSuccessful = await this.voidDcJes(Object.keys(this.jesToVoid))
          if (!voidingSuccessful) {
            throw new Error('Unable to save distribution changes: could not void previous DC Journal Entries')
          }
        }
        await this.refreshTransaction() // Manually refresh this transaction
        await this.restoreLines(linesToPreserve) // Restore the changes made to this.lines and make sure the change tracker picks up the changes
        this.lineDeltas = { ...this.lineDeltas, ...deltas }
        await this.createDcJe()
        this.showDcConfirmationDialog(false)
      } catch (e) {
        console.log(e)
        this.$notify({ title: 'Error saving distribution changes', message: e.message, type: 'error' })
      } finally {
        this.creatingDc = false
      }
    },
    /**
     * Creates a Journal Entry to offset/"show ins and outs" of a distribution change.
     * @return {Promise<void>}
     */
    async createDcJe () {
      const handleErr = (e, jeId = null) => {
        if (jeId) {
          const url = `/jes/${jeId}`
          this.showNewTransactionBox('Error', 'Offset Journal Entry created, but one or more lines failed to save', 'error', url)
        } else {
          this.$notify({ title: 'Error', message: `Offset Journal Entry not created${e ? ': ' + e : ''}`, type: 'error' })
        }
      }
      try {
        const lines = await this.buildDcJeLines()
        const header = await this.buildDcJeHeader(lines)
        this.isSaving = true
        this.$notify({ title: 'Working...', message: 'Creating Journal Entry to offset distribution changes... Please wait a moment.', type: 'info' })
        let sourcecode = this.getAllSourcecodes.find(v => v.code === 'DC')
        if (!sourcecode) {
          sourcecode = await this.fetchOrCreateDcSourcecode()
        }
        if (sourcecode && sourcecode.id) {
          header.source_code = sourcecode.id
        }
        const headerResp = await this.$store.dispatch('saveJe', {...header, lines})
        if (!(headerResp && headerResp.id)) {
          throw new Error('Could not save distribution change JE header')
        }
        if (!(headerResp && headerResp.lines)) {
          handleErr('Could not save distribution change JE lines', headerResp.id)
        }
        this.isDCUpdated = true
        const url = `/jes/${headerResp.id}`
        const postResp = await this.$store.dispatch('updateJe', { id: headerResp.id, data: { status: 'POSTED' } })
        if (!(postResp && postResp.status === 'POSTED')) { // posting the DC JE failed, inform user
          this.showNewTransactionBox('Warning!', 'Distribution change Journal Entry was created, but could not be posted.', 'warning', url)
        } else {
          this.showNewTransactionBox('Success!', 'Distribution change Journal Entry created and posted.', 'success', url)
        }
        this.headerDeltas = {}
        this.lineDeltas = {}
        await this.refreshTransaction()
      } catch (e) {
        handleErr(e)
        console.log(e)
      } finally {
        this.setChangeFlags()
        this.$refs['distribution-lines'].updateOriginalLineFlags()
        this.headerDeltas = Object.assign({})
        this.lineDeltas = Object.assign({})
      }
    },
    /**
     * Refreshes a transaction by re-fetching it from the API and re-cycling its data through the store, which causes
     * a re-render of the component.
     * TODO: this will eventually need to be RESTful
     * @return {Promise<null>}
     */
    async refreshTransaction () {
      try {
        this.ready = true
        let resp = null
        const payload = { query: { id: this.originalHeader.id, compact_lines: true }, storeLines: true }
        switch (this.transactionType.name) {
          case 'ap':
            resp = await this.$store.dispatch('fetchCompactLineApsWithQuery', payload)
            break
          case 'je':
            resp = await this.$store.dispatch('fetchCompactLineJesWithQuery', payload)
            break
          case 'pc':
            resp = await this.$store.dispatch('fetchCompactLinePcsWithQuery', payload)
        }
        return resp
      } catch (e) {
        console.log(e)
      }
    },
    /**
     * Restores the state of distribution lines before voiding the DC JE and refreshing the transaction.
     * @param preservedLines
     */
    async restoreLines (preservedLines) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (this.$refs && this.$refs['distribution-lines']) {
            const distLines = this.$refs['distribution-lines']
            this.lines = []
            distLines.lines = preservedLines
            distLines.syncLinesWithParent(distLines.getValidLines())
          }
          resolve()
        })
      })
    },
    /**
     * Builds split Trx lines. Iterates through the lines, first checking whether the line was splited. If it was splited, creates
     * transaction lines If it was not splited, skip to update changes.
     * @return {[]}
     */
    buildSplitTrxLines () {
      let trxLines = []
      let lineSplited = false
      const skipLines = []
      const skipOldLines = []
      const buildSplittingLines = (filteredSet) => {
        return filteredSet.sort((a, b) => a.line_number - b.line_number)
          .map(l => {
            l.form.line_number = l.line_number
            skipLines.push(l.line_number)
            return l
          })
      }
      this.lines.forEach(line => {
        if (skipLines.includes(line.line_number)) return
        if (line.splitBy && line.splitBy.length) { // and was split
          lineSplited = true
          line.deleteLine = true
          if (!this.lineDeltas.hasOwnProperty('delete')) {
            this.lineDeltas['delete'] = []
          }
          this.lineDeltas['delete'].push(line)
          const newSplittingLines = buildSplittingLines(this.lines.filter(l => line.splitBy.includes(l.line_number)))
          trxLines = [...trxLines, ...newSplittingLines]
        } else {
          if (!lineSplited) {
            skipOldLines.push(line.line_number)
          }
          trxLines.push(line)
        }
      })
      trxLines = trxLines
        .filter(line => !line.deleteLine)
        .map((v, i) => {
          v.form.line_number = i + 1
          return this.formatNewLine(v.form)
        })
        .sort((a, b) => a.line_number > b.line_number)
      const saveLines = trxLines.filter(l => !skipOldLines.includes(l.line_number))
      return saveLines
    },
    /**
     * Builds DC JE lines. Iterates through the lines, first checking whether the line was changed. If it was changed, creates
     * offset lines and offsetting lines according to whether the line was split and in how many ways it was split. If it was
     * not changed, determines whether there are previous DC JEs for this round of changes and whether the line was part of one.
     * @return {[]}
     */
    async buildDcJeLines () {
      let dcJeLines = []
      let lineNumber = 1
      const deltaIds = Object.keys(this.lineDeltas).filter(key => key !== 'new').sort((a, b) => a - b)
      const changedDcJes = [...new Set(deltaIds.filter(id => this.getJeLineById(id).id).map(id => this.getJeLineById(id).transaction_id))] // gives you a list of dc jes that will be modified as a results of this round of dcs
      const skipLines = []
      const idType = (form) => form.hasOwnProperty('original_line_id') ? 'original_line_id' : 'id'
      const buildSplittingLines = (line, orig, filteredSet) => {
        const { id, meta_type } = orig.form
        return filteredSet.sort((a, b) => a.line_number - b.line_number)
          .map(l => {
            skipLines.push(l.line_number)
            const splittingLine = this.buildSplittingLine({ ...l.form, id, meta_type }, lineNumber, l.line_number - 1)
            lineNumber++
            return splittingLine
          })
      }
      for (const line of this.lines) {
        if (skipLines.includes(line.line_number)) continue
        let original = this.originalLines.find(orig => orig.form.id === parseInt(line.form[idType(line.form)]))
        let id = parseInt(line.form[idType(line.form)])
        if (!(original && original.hasOwnProperty('form')) && id) {
          let temp1 = await this.$store.dispatch('fetchLinesById', {type: this.transactionType.name, id: id})
          original = { form: temp1['entities']['lines'][id] }
        }
        const wasChanged = deltaIds.includes(String(line.form.id)) // whether the line was changed since the transaction was last saved
        const deltaJeId = line.form.original_line_id ? this.getJeLineById(line.form.id).transaction_id : null // the id of the corresponding dc je, if any
        let original_line = line.line_number
        const conditions = [!(line.splitBy || line.isSplittingLine), !!(line.splitBy && line.splitBy.length)]
        if (wasChanged && (conditions[0] || conditions[1])) { // if the line was changed
          if (conditions[0]) { // and was not split
            dcJeLines.push(this.buildOffsetLine(line.form, original.form.meta_type, lineNumber, original_line))
            lineNumber++
            dcJeLines.push(this.buildOffsettingLine(original.form, lineNumber))
            lineNumber++
          } else if (conditions[1]) { // and was split
            dcJeLines.push(this.buildOffsettingLine(original.form, lineNumber))
            lineNumber++
            const newSplittingLines = buildSplittingLines(line, original, this.lines.filter(l => line.splitBy.includes(l.line_number)))
            const oldSplittingLines = buildSplittingLines(line, original, this.lines.filter(l => l.isSplittingLine && l.splits === original.form.id && l.form.id !== line.form.id))
            dcJeLines = [...dcJeLines, ...newSplittingLines, ...oldSplittingLines]
          }
        } else if (changedDcJes.includes(deltaJeId)) { // if the line was not changed but a dc je exists
          let correspondingJeLine = this.getJeLinesByTransactionId(deltaJeId)[line.form.id]
          let offsetLine = null
          // correspondingJeLine.meta_data = correspondingJeLine && correspondingJeLine.meta_data && typeof correspondingJeLine.meta_data === 'string' ? JSON.parse(correspondingJeLine.meta_data || '{}') : correspondingJeLine.meta_data
          if (correspondingJeLine.meta_data.original_line) {
            offsetLine = Object.values(this.getJeLinesByTransactionId(deltaJeId)).find(l => parseInt(l.id) === parseInt(line.form.id))
          }
          // offsetLine.meta_data = offsetLine && offsetLine.meta_data && typeof offsetLine.meta_data === 'string' ? JSON.parse(offsetLine.meta_data || '{}') : offsetLine.meta_data
          if (offsetLine && offsetLine.meta_data) {
            dcJeLines.push(this.buildOffsettingLine(original.form, lineNumber))
            lineNumber++
            for (const l of Object.values(this.getJeLinesByTransactionId(deltaJeId))) {
              // l.meta_data = l && l.meta_data && typeof l.meta_data === 'string' ? JSON.parse(l.meta_data || '{}') : l.meta_data
              const offsetting = this.lines.find(v => v.form.id === l.id)
              if (l.meta_data.original_line === offsetLine.meta_data.original_line && !l.meta_data.offset_attempt) {
                dcJeLines.push(this.buildOffsetLine(offsetting.form, offsetLine.meta_data.original_line_type, lineNumber, original_line))
                original_line = original_line + 1
                lineNumber++
                skipLines.push(offsetting.line_number)
              }
            }
          }
        }
      }
      return dcJeLines
    },
    buildSplittingLine (form, lineNumber, original_line_number) {
      return {
        account: form.account.value,
        amount_decimal: form.amount_decimal,
        description: form.description,
        other_name: form.other_name,
        location: form.location.value,
        series: form.series.value,
        set: form.set.value,
        tax_code: form.tax_code.value,
        free_field_1: form.free_field_1.value,
        free_field_2: form.free_field_2.value,
        free_field_3: form.free_field_3.value,
        free_field_4: form.free_field_4.value,
        asset: form.asset,
        third_party_vendor: form.third_party_vendor.value,
        line_number: lineNumber,
        meta_data: {
          original_line: form.id,
          original_line_type: form.meta_type,
          original_line_number: original_line_number
        },
        is_offset: false
      }
    },
    /**
     * Builds a DC JE offset line (the new line representing the actual dist change)
     * @param form
     * @param originalType
     * @param lineNumber
     */
    buildOffsetLine (form, originalType, lineNumber, original_line_number) {
      const idType = form.hasOwnProperty('original_line_id') ? 'original_line_id' : 'id'
      return {
        account: form.account.value,
        amount_decimal: form.amount_decimal,
        description: form.description,
        other_name: form.other_name,
        location: form.location.value,
        series: form.series.value,
        set: form.set.value,
        tax_code: form.tax_code.value,
        free_field_1: form.free_field_1.value,
        free_field_2: form.free_field_2.value,
        free_field_3: form.free_field_3.value,
        free_field_4: form.free_field_4.value,
        asset: form.asset,
        third_party_vendor: form.third_party_vendor.value,
        line_number: lineNumber,
        relieves: form.relieves && typeof form.relieves === 'object' ? form.relieves.value : form.relieves,
        meta_data: {
          original_line: form[idType],
          original_line_type: originalType,
          original_line_number: original_line_number
        },
        is_offset: false
      }
    },
    /**
     * Builds a DC JE offsetting line (the line which offsets the line to which a DC was made)
     * @param form
     * @param lineNumber
     */
    buildOffsettingLine (form, lineNumber) {
      return {
        account: form.account,
        amount_decimal: (form.amount_decimal * -1).toString(),
        description: form.description,
        other_name: form.other_name,
        series: form.series || this.getDefaultSeries.id,
        location: form.location || this.getDefaultLocation.id,
        set: form.set || this.getDefaultSet.id,
        tax_code: form.tax_code,
        free_field_1: form.free_field_1,
        free_field_2: form.free_field_2,
        free_field_3: form.free_field_3,
        free_field_4: form.free_field_4,
        asset: form.asset,
        third_party_vendor: form.third_party_vendor,
        line_number: lineNumber,
        meta_data: {
          original_line: form.id,
          original_line_type: form.meta_type,
          offset_attempt: true
        },
        relieves: form.relieves && typeof form.relieves === 'object' ? form.relieves.value : form.relieves,
        is_offset: true
      }
    },
    /**
     * Builds DC JE header
     * @param offsetJeLines
     * @return {object}
     */
    buildDcJeHeader (offsetJeLines) {
      const debits = offsetJeLines.reduce((a, b) => a + (parseFloat(b.amount_decimal) > 0 ? parseFloat(b.amount_decimal) : 0), 0).toFixed(2)
      const credits = offsetJeLines.reduce((a, b) => a + (parseFloat(b.amount_decimal) < 0 ? parseFloat(b.amount_decimal) : 0), 0).toFixed(2)
      return {
        batch: this.form.batch.value,
        period: this.form.period.value || this.form.period || null,
        description: `DC for ${this.originalHeader.ledger}`,
        number: this.form.number,
        debits_decimal: debits,
        credits_decimal: credits,
        original_trx_number: this.originalHeader.ledger,
        effective_date: getSimpleDate(new Date(), 'yyyy-mm-dd'),
        date: moment(new Date()).format('YYYY-MM-DD'),
        lines: []
      }
    },
    /**
     * Refreshes cached DC JEs after a distribution change is made
     * @return {Promise<void>}
     */
    async refreshCachedDcJes () {
      try {
        let offsetJePromises = []
        const offsetPromises = this.lines
          .filter(v => v.offset_by)
          .map(v => this.$store.dispatch('fetchLinesById', { type: 'je', id: v.offset_by }))
        if (offsetPromises && offsetPromises.length) {
          const offsetResponse = await Promise.all(offsetPromises)
          let jes = []
          offsetResponse.forEach(resp => {
            Object.values(resp.entities.lines).forEach(line => jes.push(line.transaction_id))
          })
          jes = [...new Set(jes)]
          offsetJePromises = await Promise.all(jes.map(id => {
            return this.$store.dispatch('fetchJeById', { id, storeLines: true })
              .then((resp) => {
                if (resp) {
                  const jeLines = Object.values(this.$store.getters.getJeLinesByTransactionId(id))
                  this.$store.dispatch('findDependencies', {lines: jeLines})
                }
              })
              .then(deps => Promise.all(deps))
          }))
        }
        return await Promise.all(offsetJePromises) // At this point, you should have the relevant JEs and their lines
      } catch (e) {
        console.log(e)
        return e
      }
    },
    /**
     * Finds all DC JEs associated with a given transaction. Typically called when voiding the transaction itself.
     * @return {Promise<Array>}
     */
    async findCorrespondingDcJes () {
      try {
        const { getDcSourceCode, getJeById } = this.$store.getters
        let dcJeIds = this.originalLines // Iterate through this.originalLines
          .filter(line => { // Filter out lines that do not contain both a transaction_id AND offset_by value
            const { transaction_id, offset_by } = line.form
            return transaction_id && !isNaN(parseInt(transaction_id)) && offset_by && !isNaN(parseInt(offset_by)) && (parseInt(transaction_id) !== parseInt(this.originalHeader.id))
          })
          .map(line => line.form.transaction_id) // Isolate the transaction_id
        if (!(dcJeIds && Array.isArray(dcJeIds) && dcJeIds.length)) { // If no lines contain a transaction_id property, then there is no DC JE to void
          return []
        }
        dcJeIds = [...new Set(dcJeIds)] // Remove duplicate ids
        const dcJes = () => dcJeIds
          .map(dcJeId => getJeById(dcJeId))
          .filter(dcJe => dcJe.source_code === getDcSourceCode.id)
          .map(dcJe => dcJe.id)
        if (!dcJes().length) { // DC JEs may not be loaded in cache, so fetch any that might be missing just in case
          const dcJeRequests = dcJeIds.map(id => this.$store.dispatch('fetchJeById', id))
          await Promise.all(dcJeRequests)
        }
        return dcJes() // call dcJes function again. If we had to check the API but found nothing, then this list will be an empty array
      } catch (e) {
        console.log(e)
        return []
      }
    },
    /**
     * Voids DC JEs and posts the resulting "voiding" transactions
     * @param dcJeIds   The IDs of the DC JEs that should be voided
     * @return {Promise<boolean>}
     */
    async voidDcJes (dcJeIds = []) {
      try {
        this.voidingDcJes = true
        const voidRequests = dcJeIds.map(id => this.$store.dispatch('updateJe', { id, data: { status: 'VOIDED' } }))
        const voidResponses = await Promise.all(voidRequests) // Void the actual DC JEs
        if (!voidResponses.length) {
          throw new Error(`Error voiding distribution change JEs ${voidRequests.toString()}`)
        }
        const vxJeRequests = []
        const vxSourceCodeId = this.getAllSourcecodes.find(sc => sc.code === 'VX').id // Get the source code id for "VX" (voiding transactions)
        voidResponses.forEach(dc => { // Construct array of async requests to fetch the "voiding" (VX) transactions
          const voided = this.getJeById(dc.id)
          const query = { original_trx_number: voided.ledger, source_code: vxSourceCodeId }
          vxJeRequests.push(this.$store.dispatch('fetchJesWithQuery', query))
        })
        const vxJeResponses = await Promise.all(vxJeRequests) // Fetch the VX transactions
        if (vxJeResponses.length) {
          const postVxJeRequests = vxJeResponses // Construct array of async requests to post the "voiding" (VX) transactions we just fetched
            .filter(({ results }) => results[0].source_code && results[0].source_code === vxSourceCodeId)
            .map(({ results }) => this.$store.dispatch('updateJe', { id: results[0].id, data: { status: 'POSTED' } }))
          const postVxJeResponses = await Promise.all(postVxJeRequests) // Post the VX transactions
          if (!postVxJeResponses.length) {
            throw new Error(`Error posting VX JEs${voidRequests.length ? 'after voiding DC JEs with IDs ' + voidRequests.toString() : ''}`)
          }
          return true
        } else {
          throw new Error(`Error fetching VX JEs after voiding DC JEs with IDs ` + voidRequests.toString())
        }
      } catch (e) {
        console.log(e)
        return false
      } finally {
        this.voidingDcJes = false
      }
    },
    /**
     * Governs what happens when the 'audit' button is clicked
     * @param type
     */
    handleAuditBtnClick (type) {
      if (this.$refs['distribution-lines'].errors.length) {
        this.$notify({ title: 'Error saving distribution changes', message: 'Incomplete lines', type: 'error' })
        return
      }
      const header = this.originalHeader
      if (!header.hasOwnProperty('transactionType') || !header.transactionType) {
        header.transactionType = type
      }
      const loader = this.$loading({
        lock: true,
        fullscreen: true,
        text: 'Auditing transaction...',
        customClass: 'trx-action-loader'
      })
      let promise
      switch (type) {
        case 'Accounts Receivable':
          promise = createBillPdf(this, header, 'AUDITED')
          break
        case 'Bill Payment':
          this.headerDeltas.status = 'AUDITED'
          promise = this.saveHeaderChangesOnly('Bp', true, null)
          break
        case 'Pre Payment':
          this.headerDeltas.status = 'AUDITED'
          promise = this.saveHeaderChangesOnly('Pp', true, null)
          break
        default:
          promise = createAuditReport(this, header)
      }
      Promise.resolve(promise).then(() => loader.close(), () => loader.close())
    },
    handlePostReportBtnClick (type) {
      let header = this.originalHeader
      getPostingReport(this, header, true)
    },
    /**
     * Governs what happens when the 'post' button is clicked
     * @param type
     * @param confirmed
     */
    handlePostBtnClick (type) {
      if (this.$refs['distribution-lines'].errors.length) {
        this.$notify({ title: 'Error saving distribution changes', message: 'Incomplete lines', type: 'error' })
        return
      }
      const header = this.originalHeader
      if (!header.hasOwnProperty('transactionType') || !header.transactionType) {
        header.transactionType = type
      }
      const isArPrintOnly = type === 'Accounts Receivable' && ['POSTED', 'CLOSED'].includes(header.status)
      const loader = this.$loading({
        lock: true,
        fullscreen: true,
        text: isArPrintOnly ? 'Generating...' : 'Posting transaction...',
        customClass: 'trx-action-loader'
      })
      let promise
      switch (type) {
        case 'Accounts Receivable':
          promise = createBillPdf(this, header, isArPrintOnly ? null : 'POSTED')
          break
        case 'Bill Payment':
          this.headerDeltas.status = 'POSTED'
          promise = this.saveHeaderChangesOnly('Bp', true, null)
          break
        case 'Pre Payment':
          this.headerDeltas.status = 'POSTED'
          promise = this.saveHeaderChangesOnly('Pp', true, null)
          break
        default:
          promise = createPostingReport(this, header)
      }
      Promise.resolve(promise).then(() => loader.close(), () => loader.close())
    },
    /**
     * Handles saving header changes only
     * @param type          The transaction type ('Ap', 'Pc', 'Je', 'Po', 'Ar', 'Bp', 'Pp')
     * @param saveOnly      Whether to prevent any further action from being taken after successful save
     * @param reportToRun   Whether to run a report after successful save
     */
    async saveHeaderChangesOnly (type, saveOnly, reportToRun) {
      try {
        this.ready = true
        this.isSaving = true
        const resp = await this.submitHeaderChanges(type)
        if (!(resp && resp.id)) {
          throw new Error('Error saving header changes')
        }
        this.changesWereSaved = true
        this.hasBeenChanged = false
        if (reportToRun) {
          this.runReport(reportToRun, type)
        } else {
          this.isSaving = false
          this.handlePostSubmit(type, saveOnly)
        }
        return resp
      } catch (e) {
        this.isSaving = false
        console.log(e)
      }
    },
    /**
     * Handles saving line changes only
     * @param type          The transaction type ('Ap', 'Pc', 'Je', 'Po')
     * @param saveOnly      Whether to prevent any further action from being taken after successful save
     * @param reportToRun   Whether to run a report after successful save
     */
    async saveLineChangesOnly (type, saveOnly, reportToRun) {
      const { originalHeader } = this
      const cachedStatus = originalHeader.status === 'AUDITED' ? 'OPEN' : originalHeader.status
      try {
        this.ready = false
        this.isSaving = true
        const resp = await this.$store.dispatch(`update${type}`, { id: originalHeader.id, data: { status: (type === 'Po') ? originalHeader.status : 'DRAFT' } })
        if (!(resp && resp.id)) {
          throw new Error('Unable to set transaction to DRAFT')
        }
        this.submitLineChanges(saveOnly, reportToRun, cachedStatus)
      } catch (e) {
        this.isSaving = false
        console.log(e)
        return e
      }
    },
    /**
     * Handles saving header and line changes
     * @param type          The transaction type ('Ap', 'Pc', 'Je', 'Po')
     * @param saveOnly      Whether to prevent any further action from being taken after successful save
     * @param reportToRun   Whether to run a report after successful save
     */
    async saveHeaderAndLineChanges (type, saveOnly, reportToRun) {
      const cachedStatus = this.originalHeader && this.originalHeader.status ? this.originalHeader.status : 'DRAFT'
      this.headerDeltas.status = (type === 'Po') ? this.originalHeader.status : 'DRAFT'
      this.isSaving = true
      this.ready = false
      try {
        const resp = await this.submitHeaderChanges(type)
        if (!(resp && resp.id)) {
          throw new Error('Error setting transaction to DRAFT')
        }
        this.submitLineChanges(saveOnly, reportToRun, cachedStatus)
      } catch (e) {
        this.isSaving = false
        this.ready = true
        console.log(e)
      }
    },
    /*
    * Validates header changes, calls Vuex action to submit header changes to the API
    * and redirects the user with a success/failure notification
    * @param {VueComponent} this
    * @param {String} entityType
    * @param {Object} headerDeltas
    * @param {Boolean} saveOnly (whether to return to list view after save or save only and stay in current view)
    * @return void
   */
    async submitHeaderChanges (entityType) {
      const { fields, $store, $route } = this
      let headerDeltas = { ...this.headerDeltas }
      const { id } = $route.params
      const errors = validateDataEntryModels(headerDeltas, fields)
      headerDeltas = this.shouldSetTransactionToDraft(headerDeltas)
      headerDeltas = this.formatEmptyDateString(headerDeltas)
      if (errors.messages) {
        return errors.messages.map((v, i) => notify(this, 'warning', v, (i * 100)))
      }
      try {
        const resp = await $store.dispatch(`update${entityType}`, { id, data: headerDeltas })
        if (!resp && resp.id) {
          throw new Error()
        }
        this.headerDeltas = Object.assign({}) // Reset header deltas after successful save
        this.$store.dispatch('setHeaderForm', this.form)
        if (headerDeltas.effective_date && this.dcJes.length && this.dcJes.filter(dc => dc.id).length) {
          await this.updateAssociatedDcJes({ effective_date: headerDeltas.effective_date })
        }
        return resp
      } catch (e) {
        console.log(e)
        this.isSaving = false
        if (e.body && e.body.code && e.body.code === 'PNDUP') {
          this.handleDuplicatepayments(e.body.payment_confirmation_number, true)
        } else {
          notify(this, 'error', `Error saving ${entityType.toUpperCase()}`)
        }
      }
    },
    /**
     * Handles saving header and line changes
     * @param type          The transaction type ('Ap', 'Pc', 'Je', 'Po')
     * @param saveOnly      Whether to prevent any further action from being taken after successful save
     * @param reportToRun   Whether to run a report after successful save
     */
    async saveTransactionChanges (type, saveOnly, reportToRun, dcSave = false, linesToPreserve = []) {
      this.isSaving = true
      this.ready = false
      this.isSaving = true
      const { fields, originalHeader } = this
      let headerDeltas = { ...this.headerDeltas }
      const cachedStatus = originalHeader.status === 'AUDITED' ? 'OPEN' : originalHeader.status
      let lines = []
      let linesToBeUpdated = linesToPreserve && linesToPreserve.length ? linesToPreserve : this.lines
      const deltas = this.lineDeltas
      const errors = validateDataEntryModels(headerDeltas, fields)
      headerDeltas = this.formatEmptyDateString(headerDeltas)
      if (errors.messages) {
        return errors.messages.map((v, i) => notify(this, 'warning', v, (i * 100)))
      }
      const afterSave = async () => {
        if (deltas && deltas.delete && deltas.delete.length) { // Handle lines that need to be deleted
          await this.deleteLines()
        }
        if (Object.keys(deltas).length) {
          await this.restoreOriginalStatus(`${capitalizeFirstLetter(this.transactionType.name)}`, cachedStatus || 'OPEN')
        }
        await this.refreshTransaction()
        this.headerDeltas = Object.assign({}) // Reset header deltas after successful save
        this.lineDeltas = Object.assign({})
        this.ready = true
        this.setChangeFlags()
        this.$store.dispatch('setHeaderForm', this.form)
        if (headerDeltas.effective_date && this.dcJes.length && this.dcJes.filter(dc => dc.id).length) {
          await this.updateAssociatedDcJes({ effective_date: headerDeltas.effective_date })
        }
        if (reportToRun) {
          this.runReport(reportToRun, type)
        } else {
          this.handlePostSubmit(type, saveOnly)
        }
      }
      let linesplitted = linesToBeUpdated.some((l) => l.splitBy)
      if (['DRAFT', 'OPEN', 'AUDITED'].includes(originalHeader.status) && linesplitted) {
        lines = this.buildSplitTrxLines()
      } else {
        lines = linesToBeUpdated
        .filter(line => !line.deleteLine)
        .map((v, i) => {
          v.form.line_number = i + 1
          return this.formatNewLine(v.form)
        })
        .sort((a, b) => a.line_number > b.line_number)
      }
      let filteredLines = linesplitted ? lines : lines.filter(line => !line.id || (line.id && Object.keys(deltas).includes(`${line.id}`)))
      filteredLines.forEach((line) => {
        if (line.hasOwnProperty('recon')) delete line.recon
        if (!line || !line.account) return
        if (typeof line.account === 'object' && !Object.keys(line.account).length) line['account'] = ''
      })
      if (filteredLines && filteredLines.length) {
        headerDeltas.status = (type === 'Po') || (type === 'Ap') ? originalHeader.status : 'DRAFT'
      }
      // Only for delete line with header amount adjust and matches the lines amount
      if (deltas && Object.keys(deltas).length === 1 && deltas.delete && headerDeltas.hasOwnProperty('amount_decimal') && filteredLines.length === 0 && ['DRAFT', 'OPEN'].includes(originalHeader.status)) {
        filteredLines = lines
        headerDeltas.status = 'DRAFT'
      }
      let payload = Object.assign({ ...headerDeltas }, { lines: filteredLines.length ? filteredLines : undefined })
      if (dcSave) {
        this.$store.dispatch(`updateTransactionLine`, { data: payload.lines, id: this.originalHeader.id, type: this.transactionType.name })
          .then(async r => {
            if (r && r.length) {
              if (deltas && deltas.delete && deltas.delete.length) { // Handle lines that need to be deleted
                await this.deleteLines()
              }
              await this.refreshTransaction()
              this.headerDeltas = Object.assign({}) // Reset header deltas after successful save
              this.lineDeltas = Object.assign({})
              this.ready = true
              this.setChangeFlags()
            } else {
              this.isSaving = false
            }
            return r
          }).catch(e => {
            console.log(e)
            this.isSaving = false
            notify(this, 'warning', `Error saving ${type} entry. Please try saving again.`)
          })
      } else {
        this.$store.dispatch(`update${capitalizeFirstLetter(type)}`, { data: payload, id: this.originalHeader.id })
        .then(async r => {
          if (r && r.id) {
            await this.getLineRelieves(r.lines)
            afterSave()
          } else {
            this.isSaving = false
          }
          return r
        }).catch(e => {
          console.log(e)
          this.isSaving = false
          if (e.body && e.body.code && e.body.code === 'PNDUP') {
            this.handleDuplicatepayments(e.body.payment_confirmation_number, true)
          } else {
            notify(this, 'warning', `Error saving ${type} entry. Please try saving again.`)
          }
        })
      }
    },
    async getLineRelieves (lines) {
      const poLineIds = lines.map(line => line.relieves)
      if (poLineIds.length) {
        if (poLineIds.length > 500) {
          for (let i = 0; i < poLineIds.length; i += 500) {
            const chunk = [...new Set(poLineIds.slice(i, i + 500))]
            await this.$store.dispatch('fetchPoLinesWithQuery', { id__in: chunk, page_size: chunk.length })
          }
        } else {
          await this.$store.dispatch('fetchPoLinesWithQuery', { id__in: [...new Set(poLineIds)], page_size: poLineIds.length })
        }
      }
    },
    /**
     * Updates the DC JEs associated with this transaction with whatever payload is passed in the data argument. If the
     * PATCH request(s) fail, displays a notification that, when clicked, re-attempts the PATCH requests with the exact
     * same payload. Does not allow more than one retry.
     * @param data
     * @param retryAttempt
     * @return {Promise}
     */
    async updateAssociatedDcJes (data, retryAttempt = false) {
      try {
        if (this.dcJeUpdateErrMsg && typeof this.dcJeUpdateErrMsg.close === 'function') {
          this.dcJeUpdateErrMsg.close()
        }
        this.dcJeUpdateErrMsg = null
        const requests = this.dcJes.map(({ id }) => this.$store.dispatch('updateJe', { id, data }))
        const resps = await Promise.all(requests)
        if (!resps.every(resp => resp.id)) {
          throw new Error()
        }
        return resps
      } catch (e) {
        console.log(e)
        const msg = retryAttempt ? 'Please contact support at support@revolutiones.com' : 'Click to retry.'
        const opts = {
          title: 'Error updating DC Journal Entries',
          message: `Could not update one or more associated distribution change journal entries. ${msg}`,
          type: 'warning',
          duration: retryAttempt ? 10000 : 0,
          onClick: !retryAttempt ? async () => await this.updateAssociatedDcJes(data, true) : null
        }
        this.dcJeUpdateErrMsg = this.$notify(opts)
      } finally {
        if (retryAttempt) {
          setTimeout(() => {
            this.dcJeUpdateErrMsg = null
          }, 11000)
        }
      }
    },
    /**
     * PATCHes an existing line
     * @param lines
     * @return {Promise<*>}
     */
    async updateExistingLines (lines) {
      try {
        const updatePromises = lines.map(line => this.$store.dispatch('updateLine', { type: this.transactionType.name, id: line.id, line }))
        const updateResp = await Promise.all(updatePromises)
        if (!(updateResp && updateResp.length)) {
          throw new Error('Error updating lines')
        }
        return updateResp
      } catch (e) {
        console.log(e)
        return e
      }
    },
    /**
     * Resets all flags that indicate the current state of this transaction to what they should be after
     * a successful save.
     */
    setChangeFlags () {
      this.changesWereSaved = true
      this.hasBeenChanged = false
      this.linesHaveBeenChanged = false
      this.isSaving = false
      if (this.transactionType.name === 'je') {
        this.transactionWasBalanced = isJeBalanced(this)
      }
      if (this.$refs && this.$refs['distribution-lines']) {
        this.$refs['distribution-lines'].resetAllLineChanges()
      }
    },
    /*
    * For each line changed, calls Vuex action to submit distribution changes to the API
    * and redirects the user with a success/failure notification
    * @param {VueComponent} this
    * @param {String} entityType
    * @param {Object} headerDeltas
    * @param {Boolean} saveOnly (whether to return to list view after save or save only and stay in current view)
    * @return void
   */
    async submitLineChanges (saveOnly, reportToRun, cachedStatus = 'OPEN') {
      const type = this.transactionType.name
      const { id } = this.$route.params
      const deltas = this.lineDeltas
      this.ready = false
      const afterSave = async () => {
        this.lineDeltas = Object.assign({})
        this.ready = true
        await this.restoreOriginalStatus(`${capitalizeFirstLetter(this.transactionType.name)}`, cachedStatus)
        this.setChangeFlags()
        if (reportToRun) {
          this.runReport(reportToRun, type)
        } else {
          this.handlePostSubmit(type, saveOnly)
        }
      }
      try {
        if (deltas && deltas.delete && deltas.delete.length) { // Handle lines that need to be deleted first
          await this.deleteLines()
        }
        const lines = this.lines
          .filter(line => !line.deleteLine)
          .map((v, i) => {
            v.form.line_number = i + 1
            return this.formatNewLine(v.form)
          })
          .sort((a, b) => a.line_number > b.line_number)
        const linesToPatch = lines.filter(line => Object.keys(deltas).includes(`${line.id}`)) // PATCH lines that need to be updated
        if (linesToPatch && linesToPatch.length) {
          await this.updateExistingLines(linesToPatch)
        }
        const linesToPost = lines.filter(line => !line.id) // POST new lines
        if (linesToPost && linesToPost.length) {
          this.saver = new LineSaver(this.$store, linesToPost, { lineType: `${this.transactionType.name}`, trxId: id })
          this.saver.on('success', async () => afterSave())
          this.saver.on('failure', () => {
            this.isSaving = false
            notify(this, 'warning', 'Error saving lines')
          })
          this.saver.saveLines()
        } else {
          afterSave()
        }
      } catch (e) {
        console.log(e)
        this.ready = true
      }
    },
    /**
     * Creates delete requests
     * @return {*}
     */
    async deleteLines () {
      const type = this.transactionType.name
      const promises = this.lineDeltas.delete.map(line => this.$store.dispatch('deleteLine', { id: line.form.id, type }))
      try {
        const resp = await Promise.all(promises)
        if (!(resp && resp.length === this.lineDeltas.delete.length)) {
          throw new Error('Error deleting lines')
        }
        const deletedLines = resp.map(v => parseInt(v.deletedLine))
        this.lines = this.lines.filter(line => !deletedLines.includes(parseInt(line.form.id))) // When a line is successfully deleted, remove it from this.lines
        this.lineDeltas.delete = this.lineDeltas.delete.filter(line => deletedLines.includes(line.form.id)) // And remove it from the deltas
        if (!this.lineDeltas.delete.length) { // If there are no more lines to be deleted, delete the deltas 'delete' property
          delete this.lineDeltas.delete
        }
        return resp
      } catch (e) {
        console.log(e)
        return e
      }
    },
    /**
     * Removes lines marked for deletion and re-numbers the remaining lines
     */
    removeDeletedLines () {
      const deleted = []
      if (this.lineDeltas.new && this.lineDeltas.new.length) {
        this.lineDeltas.new = this.lineDeltas.new.filter(line => !line.deleteLine)
        this.lines = this.lines.filter(line => {
          if (!line.form.id && line.deleteLine) {
            deleted.push(line.line_number)
          }
          return !(!line.form.id && line.deleteLine)
        })
      }
      if (this.lineDeltas.new && !this.lineDeltas.new.length) {
        delete this.lineDeltas.new
      }
      return deleted
    },
    /**
     * Formats a new line, assigns it a line number, assigns it a related transaction id, and, if
     * the transaction is a PO, sets the line type to 'OPEN'
     * @param line
     * @param type
     * @param index
     * @return {*}
     */
    formatNewLine (line, type) {
      let newline = this.removeEmptyLineValues(this.cleanupLine(line))
      if (this.transactionType.name === 'ar') {
        newline = this.setMarkupAmounts(newline)
      }
      newline.transaction = parseInt(this.$route.params.id)
      if (type === 'PO' && newline.hasOwnProperty('type') && !newline.type) {
        newline.type = 'OPEN'
      }
      return { ...newline }
    },
    /**
     * Cleanup line
     * @param line
     * @return {*}
     */
    cleanupLine (line) {
      const newline = { ...line }
      for (const key in newline) {
        if (newline[key] && newline[key].hasOwnProperty('value')) {
          newline[key] = newline[key].value || null
        }
      }
      return newline
    },
    /**
     * Removes empty properties from JSON request payload
     * @param lineForm
     * @return {any}
     */
    removeEmptyLineValues (lineForm) {
      const form = { ...lineForm }
      const lineRequirements = {}
      this.lineFields.forEach(field => {
        const { required, dataType } = field
        lineRequirements[field.name] = { required: !!required, dataType }
      })
      for (const field in lineForm) {
        if (field === 'relieves_force_close' && form[field] === null) {
          delete form.relieves_force_close
        }
        if (field === 'relieves') {
          continue
        }
        if (!lineRequirements[field] && form[field]) {
          continue
        } else if (!lineRequirements[field] && form[field] === false) { // Need to explicitly check for false because if not, asset and relieves_force_close changes could fail
          continue
        } else if (!lineRequirements[field] && !form[field]) { // If field isn't required and field is cleared (''/null/undefined)
          form[field] = null
          continue
        }
        const { required, dataType } = lineRequirements[field]
        const value = lineForm[field]
        if (!required && dataType === 'number' && (value === '' || value === 'undefined')) {
          form[field] = null
        } else if (!required && dataType !== 'number' && dataType !== 'boolean' && !value) {
          form[field] = ''
        } else if (dataType === 'boolean') {
          form[field] = !!value
        }
      }
      return form
    },
    /**
     * Because the API expects an empty date to be null (will not accept ''), date field values that are cleared should
     * be set to null before being submitted.
     * @param headerDeltas
     */
    formatEmptyDateString (headerDeltas) {
      let inputFields = []
      for (const fieldGroup in this.fields) {
        if (!(this.fields[fieldGroup] && this.fields[fieldGroup].inputFields)) {
          continue
        }
        const dateFields = this.fields[fieldGroup].inputFields.filter(field => field.fieldType === 'date')
        inputFields = [...inputFields, ...dateFields]
      }
      for (const fieldName in headerDeltas) {
        if (!fieldName.includes('date')) {
          continue
        }
        const val = headerDeltas[fieldName]
        const fieldDefinition = inputFields.find(field => field.name === fieldName)
        headerDeltas[fieldName] = val === '' && fieldDefinition ? null : val
      }
      return headerDeltas
    },
    /**
     * Check each response. If it's ok, remove it from this.lineDeltas. If not, add it to list of failed lines,
     * then notify the user.
     * @param responses
     * @return {Promise}
     */
    checkLines (responses) {
      return new Promise((resolve, reject) => {
        const failed = []
        responses = flattenArrayRecursive(responses)
        responses.forEach(r => {
          if (r && r.id) {
            if (this.lineDeltas[r.id]) {
              delete this.lineDeltas[r.id]
            } else if (this.lineDeltas.new) {
              this.lineDeltas.new = this.lineDeltas.new.filter(line => line.line_number !== r.line_number)
            }
          } else if (r && r.deletedLine && r.response.ok) {
            if (this.lineDeltas.hasOwnProperty('delete')) {
              this.lineDeltas.delete = this.lineDeltas.delete.filter(line => line.form.id !== r.deletedLine)
            }
          } else {
            failed.push(r)
          }
        })
        if (this.lineDeltas && this.lineDeltas.delete && !this.lineDeltas.delete.length) {
          delete this.lineDeltas.delete
        }
        if (this.lineDeltas && this.lineDeltas.new && !this.lineDeltas.new.length) {
          delete this.lineDeltas.new
        }
        if (failed.length) {
          notify(this, 'error', `Error updating lines ${failed.map(f => f.deletedLine).toString()}`)
        } else {
          this.changesWereSaved = true
          this.hasBeenChanged = false
        }
        return resolve(true)
      })
    },
    /**
     * Runs any report that needs to be run after saving changes to lines
     * @param reportToRun
     * @param formattedType
     */
    runReport (reportToRun, formattedType) {
      const { id } = this.$route.params
      const getter = this.$store.getters[`get${capitalizeFirstLetter(formattedType)}ById`]
      reportToRun === 'audit' ? createAuditReport(this, getter(id)) : createPostingReport(this, getter(id))
    },
    /**
     * Governs what happens after a successful submit
     * @param type
     * @param saveOnly
     */
    handlePostSubmit (type, saveOnly) {
      if (!saveOnly) {
        if (this.altRoute && Object.keys(this.altRoute).length) {
          notify(this, 'success', `${type.toUpperCase()} saved`)
          this.altRoute.query = { batch: this.form.batch.value }
          return this.$router.push(this.altRoute)
        }
        notify(this, 'success', `${type.toUpperCase()} saved`)
        return this.$router.push({ name: `${type.toUpperCase()}s` })
      } else {
        if (!this.isPcReimbursementTransaction) {
          notify(this, 'success', 'Changes saved')
        }
      }
    },
    /**
     * Reassigns line numbers to existing lines when one or more lines is set to be deleted
     */
    reNumberExistingLines () {
      let newLineNumber = 1
      this.lines = this.lines.map(line => {
        if (line && line.deleteLine) return line // Skip lines that are to be deleted
        line.line_number = newLineNumber
        line.form.line_number = newLineNumber
        if (line.form.id && this.lineDeltas[line.form.id]) {
          this.lineDeltas[line.form.id].line_number = newLineNumber
        } else if (line.form.id && !this.lineDeltas[line.form.id]) {
          this.lineDeltas[line.form.id] = { line_number: newLineNumber }
        }
        newLineNumber++
        return line
      })
    },
    /**
     * Restores a transaction back to its original status
     * @param formattedType
     * @param originalStatus
     * @return {Promise.<TResult>}
     */
    async restoreOriginalStatus (formattedType, originalStatus) {
      const { id } = this.originalHeader
      const msg = `Unable to restore original transaction status from temporary change to DRAFT.
              Please try re-opening the transaction manually.`
      try {
        const resp = await this.$store.dispatch(`update${formattedType}`, { id, data: { status: originalStatus } })
        if (!(resp && resp.id)) {
          throw new Error()
        }
        return resp
      } catch (e) {
        console.log(e)
        notify(this, 'error', msg)
        return e
      }
    },
    /**
     * Determines whether a transaction should be set to draft prior to submitting changes
     * @param headerDeltas
     * @return {*}
     */
    shouldSetTransactionToDraft (headerDeltas) {
      const { originalHeader } = this
      const exempt = ['DRAFT', 'POSTED', 'CLOSED', 'VOIDED']
      const deltaCount = Object.keys(headerDeltas).length
      if (!exempt.includes(originalHeader.status) && deltaCount && !headerDeltas.hasOwnProperty('status')) {
        const protectedFields = ['amount_decimal', 'debits_decimal', 'credits_decimal', 'batch', 'vendor']
        if ((Object.keys(headerDeltas).some(v => protectedFields.includes(v)))) {
          headerDeltas.status = 'DRAFT'
          this.statusToRestore = originalHeader.status
        }
      }
      return headerDeltas
    },
    /**
     * Determines whether a transaction should be set to draft prior to submitting changes
     * @return {boolean|*|Number}
     * TODO: deprecated
     */
    shouldSetToDraft () {
      const exempt = ['POSTED', 'CLOSED', 'VOIDED']
      return (!exempt.includes(this.originalHeader.status) &&
        (!this.transactionIsBalanced || this.amountHasChanged || Object.keys(this.lineDeltas).length))
    },
    /**
     * For ARs only. Moves the value of form.markup_amount to form.markup_rate or form.markup_amount_decimal based on the value
     * of form.markup_type. This allows us to have only one column for the markup amount, which can represent a
     * percentage rate or a fixed amount, and send the correct properties to the API upon submission.
     * @param lineForm
     * @return {any}
     */
    setMarkupAmounts (lineForm) {
      const form = { ...lineForm }
      if (form.markup_type === 'AMT' && form.markup_amount) {
        form.markup_amount_decimal = form.markup_amount
        delete form.markup_rate
      } else if (form.markup_type === 'PCT' && form.markup_amount) {
        form.markup_rate = form.markup_amount
        delete form.markup_amount_decimal
      }
      delete form.markup_amount
      delete form.amount_with_markups_decimal
      return { ...form }
    },
    /**
     * Sets whether or not any line in the DistributionLines component has been changed in any way
     * This does not have anything to do with valid lines that have been synced to the parent component.
     * It simply tells you whether any change has been made to any line in the DistributionLines component.
     * @param linesHaveChanged    Whether any line has been changed
     */
    setLinesHaveBeenChanged (linesHaveChanged) {
      this.linesHaveBeenChanged = linesHaveChanged
    },
    /**
     * Focuses on the first input field
     */
    focusOnFirstField () {
      const inputs = document.getElementsByTagName('input')
      if (inputs && inputs[0]) {
        inputs[0].focus()
      }
    },
    /**
     * Line update hook. Fires whenever a line is modified.
     * @param lineNumber    The number of the line being modified
     * @param fieldName     The name of the field being modified
     * @param newValue      The new value
     * @param instance      The line component instance
     */
    lineModified ({ lineNumber, fieldName, newValue, instance = {} }) {
      if (fieldName === 'prepayment' && typeof this.setLineAcctToCustomerDepositAcct === 'function') {
        this.setLineAcctToCustomerDepositAcct(lineNumber, newValue, instance)
      }
    },
    /**
     * Customizes the order of the distribution line fields, based on what is specified in /settings, and
     * overwrites the label and/or readOnly property, if applicable.
     * @param fields
     * @return {*}
     */
    setCustomFieldOrder (fields) {
      const order = this.$store.getters.getUiSettings.LINE_FIELD_ORDER
      if (!order) {
        return fields
      }
      try {
        const parsed = JSON.parse(order)[this.transactionType.name]
        let newFields = fields.map(() => null) // create array of blank values of length of fields
        fields.forEach(field => { // if a position is specified in 'parsed' for a given field, assign the field to that position
          const position = parsed[field.name] && parsed[field.name].position ? parseInt(parsed[field.name].position) : null
          if (parsed[field.name] && position && (position <= fields.length - 1)) { // Make sure the position specified is valid
            newFields[position] = { ...field }
          }
        }) // You should now have an array that looks like [null, null, { name: 'account'... }, null, { name: 'set'... }...], etc.
        const remaining = fields.filter(field => !(newFields.map(v => v && v.name).includes(field.name))) // get left over fields that were not specified in settings
        newFields = newFields.map(field => {
          if (!field && remaining.length) { // if there isn't a field at this index
            field = Object.assign(remaining.shift()) // take the first element of the remaining unassigned fields and set it to that index
          }
          if (field && parsed[field.name] && parsed[field.name].label) { // Allow label to be overwritten
            field.label = parsed[field.name].label
          }
          if (field.hasOwnProperty('readOnly') && parsed[field.name] && parsed[field.name].hasOwnProperty('read_only')) { // Allow readOnly to be overwritten
            field.readOnly = parsed[field.name].read_only
          }
          if (parsed[field.name] && parsed[field.name].hasOwnProperty('hidden')) { // Allow hidden property to be overwritten
            field.hidden = parsed[field.name].hidden
          }
          return field
        })
        return newFields
      } catch (e) {
        if (e instanceof SyntaxError && e.message.includes('Unexpected token')) {
          console.log('Field order settings property contains invalid JSON. Error: ' + e)
        } else {
          console.log(e)
        }
        return fields
      }
    },
    /*
    * Fetches open purchase order lines for a given vendor
    * @param {VueComponent} self
    * @return void
    */
    async getVendorOpenPoLines () {
      if (this.currentVendorSelection) {
        const query = {
          transaction__status: 'POSTED',
          transaction__vendor: this.currentVendorSelection,
          type__in: 'OPEN,PARTIAL',
          page_size: pageSizes.lines.po
        }
        this.fetchingPoData = true
        try {
          const resp = await this.$store.dispatch('fetchPoLinesWithQuery', query)
          if (resp && resp.count && resp.count > pageSizes.lines.po) {
            const requests = []
            let i = 2
            while (i <= Math.ceil(resp.count / pageSizes.lines.po)) {
              query.page = i
              requests.push(this.$store.dispatch('fetchPoLinesWithQuery', query))
              i++
            }
            await Promise.all(requests)
          }
          const vendorOpenPoIds = this.getVendorById(this.currentVendorSelection).open_pos || []
          this.vendorPoLines = this.getPoLines.filter(v => vendorOpenPoIds.includes(v.transaction_id) && v.type === 'OPEN')
        } catch (e) {
          console.log(e)
          return []
        } finally {
          this.fetchingPoData = false
        }
      }
    },
    /** To set bank set, series and location values on change of batch */
    setBankAttributeLineValues (bankAttributes = null) {
      const bank = bankAttributes || this.bankAttributes || null
      if (!bank || !this.$refs['distribution-lines']) return
      const attributes = ['series', 'location', 'set']
      // $children (via @vue/compat INSTANCE_CHILDREN) returns EVERY child of lines-form, incl. non-Line
      // instances whose `.line` is undefined — reading distLine.line.amount_decimal on those threw. Keep Lines.
      const form = this.$refs['distribution-lines'].$refs['lines-form']
      const distLines = ((form && form.$children) || []).filter(c => c && c.line)
      if (distLines && distLines.length) {
        distLines.forEach((distLine) => {
          // Only update if the amount isn't there
          if (distLine.line.amount_decimal) return null
          attributes.forEach((attribute) => {
            if (bank[attribute] && bank[attribute].id) distLine.modifyLine(attribute, { label: bank[attribute].code, value: bank[attribute].id })
          })
        })
      }
    },
    /** To set batch set, series and location values on change of batch */
    setBatchAttributeLineValues (batchAttributes = null) {
      const batch = batchAttributes || this.batchAttributes || null
      if (!batch || !this.$refs['distribution-lines']) return
      const attributes = ['series', 'location', 'set', 'free_field_1', 'free_field_2', 'free_field_3', 'free_field_4', 'tax_code']
      const form = this.$refs['distribution-lines'].$refs['lines-form']
      const distLines = ((form && form.$children) || []).filter(c => c && c.line)
      if (distLines && distLines.length) {
        distLines.forEach((distLine) => {
          // Only update if the amount isn't there
          if (distLine.line.amount_decimal) return null
          attributes.forEach((attribute) => {
            if (batch[attribute] && batch[attribute].id) {
              distLine.modifyLine(attribute, { label: batch[attribute].code, value: batch[attribute].id })
            } else {
              distLine.modifyLine(attribute, { label: '', value: '' })
            }
          })
        })
      }
    }
  },
  computed: {
    ...mapGetters({
      getJeById: 'getJeById',
      getJeLineById: 'getJeLineById',
      getJeLinesByTransactionId: 'getJeLinesByTransactionId',
      getAllSourcecodes: 'getAllSourcecodes',
      getAddressById: 'getAddressById',
      getStateById: 'getStateById',
      getCityById: 'getCityById'
    }),
    /**
     * Returns a boolean indicating whether or not the initial data is loaded
     * @return {boolean}
     */
    dataLoaded () {
      return this.$store.getters.getInitDataLoadedStatus
    },
    /**
     * Determines whether the form has been changed in any way
     * @return {boolean}
     */
    anyChange () {
      return this.hasBeenChanged || this.linesHaveBeenChanged
    },
    excludedFields () {
      switch (this.transactionType.name) {
        case 'pr':
        case 'je':
          return ['debits_decimal', 'credits_decimal']
        case 'pc':
          return ['pcAccount', 'cashAccount', 'pCardAccount', 'amount_beginning_pc_decimal', 'amount_remaining_pc_decimal']
        default:
          return []
      }
    },
    /**
     * Determines whether the transaction is saveable. Returns true if the following conditions apply:
     * 1) A change has been made, either to the header, lines, or both
     * 2) The transaction is balanced
     * 3) All required header and line fields have a value
     */
    isSaveable () {
      let requiredHeaderFields = []
      for (const fieldName in this.fields) {
        if (this.fields[fieldName].inputFields) {
          requiredHeaderFields.push(...this.fields[fieldName].inputFields.filter(field => field.required))
        }
      }
      requiredHeaderFields = requiredHeaderFields.map(field => ({ name: field.name, type: field.dataType }))
      const headerRequirementsMet = requiredHeaderFields.every(field => {
        let hasVal
        const formVal = this.form[field.name]
        if (field.type !== 'boolean' && formVal && formVal.hasOwnProperty('value')) {
          hasVal = !!formVal.value
        } else if (field.type === 'boolean') {
          hasVal = true
        } else {
          hasVal = !!formVal
        }
        return hasVal
      })
      if (this.form.status === 'DRAFT' && headerRequirementsMet) {
        return true
      }
      if (!headerRequirementsMet && this.transactionIsBalanced && ['POSTED', 'CLOSED'].includes(this.originalHeader.status) && Object.keys(this.lineDeltas).length) {
        return true
      }
      if (!this.transactionIsBalanced) {
        return false
      }
      if (headerRequirementsMet && this.transaction.transactionType === 'Accounts Payable' && this.form.date && this.form.payment_date) {
        return !moment(this.form.payment_date).isBefore(moment(this.form.date))
      }
      return headerRequirementsMet
    },
    /**
     * Determines whether customer or vendor form modal should be opened
     * @return {string}
     */
    customerVendorFormType () {
      const visible = this.$store.getters.getModalVisibilityState
      if (visible.customerVendorForm && typeof visible.customerVendorForm === 'object' && visible.customerVendorForm.visible) {
        return visible.customerVendorForm.type
      }
      return 'vendor'
    },
    selectedVendor () {
      if (this.currentVendorSelection || (this.form.vendor && this.form.vendor.value)) {
        const vendorCode = this.currentVendorSelection || this.form.vendor.value
        return this.getVendorById(vendorCode)
      } else {
        return null
      }
    },
    // Return first found address as a string
    vendorAddressLine () {
      if (this.selectedVendor) {
        const addressLists = ['address_selected', 'address_contact', 'address_billing', 'address_mailing']
        for (let i = 0; i < addressLists.length; i++) {
          const v = this.selectedVendor[addressLists[i]] || undefined
          let ven

          if (!v) {
            return undefined
          } else if (!isNaN(v)) {
            ven = this.getAddressById(v)
          } else if (typeof v === 'object' && v.line1) {
            ven = v
          } else {
            return undefined
          }
          let address = ven.line1
          address += ven.line2 ? ' ' + ven.line2 : ''
          address += ven.city ? ', ' + (isNaN(ven.city) ? ven.city : this.getCityById(ven.city).name) : ''
          address += '\n'
          address += ven.state ? ', ' + (isNaN(ven.state) ? ven.state : this.getStateById(ven.state).code) : ''
          address += ven.postal ? '  ' + ven.postal : ''
          return address
        }
      } else {
        return undefined
      }
    },
    /**
     * Returns the transaction model
     * @return {object}
     */
    transaction () {
      return this.$store.getters[`get${capitalizeFirstLetter(this.transactionType.name)}ById`](this.$route.params.id)
    },
    /**
     * Returns the number of files currently uploaded
     * @return {number}
     */
    numberOfFiles: {
      get () {
        return this.transaction && this.transaction.files ? (this.fileCount + this.transaction.files.length) : 0
      },
      set (files) {
        this.fileCount = this.fileCount + files
      }
    },
    /**
     * Determines whether a DC JE should be created when a DC is made, based on whether the transaction status is
     * either POSTED or CLOSED and whether line changes have been made
     * @return {boolean|number}
     */
    shouldCreateDcJe () {
      const { status } = this.originalHeader
      return (status === 'POSTED' || status === 'CLOSED') && Object.keys(this.lineDeltas).length
    },
    /**
     * Whether to allow distribution changes to be made. We check if the transaction type is "je" in the first case of
     * the switch block. If so, we check if the form has a source_code value. If it does, we get the source_code from
     * the store and determine if it is 'DC' (distribution change). If it is, we return true to disable distribution
     * changes. Otherwise, execution falls through the switch statement and the last return statement is evaluated as
     * per normal criteria for disabling DCs.
     */
    disableDistChanges () {
      switch (this.transactionType.name) {
        case 'je':
          const { source_code } = this.form
          const { getSourcecodeById } = this.$store.getters
          const scId = source_code && source_code.value ? source_code.value : null
          if (scId && getSourcecodeById(scId) && getSourcecodeById(scId).code === 'DC') {
            return true
          }
          break
        case 'po':
          return false
        case 'ar':
        case 'bp':
        case 'pp':
          return true
      }
      return ['VOIDED'].includes(this.form.status)
    },
    /**
     * Returns a list of DCs for this trx
     */
    viewableDcs () {
      return this.originalLines.filter(line => line.form.offset_by)
    },
    /**
     * Returns the current DC JEs associated with this transaction.
     */
    dcJes () {
      const dcJeLineIds = [...new Set(this.viewableDcs.map(dc => dc.form.offset_by))]
      const dcJeLines = dcJeLineIds.map(id => this.getJeLineById(id))
      const dcJeIds = [...new Set(dcJeLines.map(line => line.transaction_id))]
      return dcJeIds.map(id => this.getJeById(id))
    }
  },
  watch: {
    /**
     * Tracks whether the form has been changed. If so, sets a store property that warns the user
     * that they will lose changes if they try to refresh the page.
     */
    anyChange () {
      this.$store.dispatch('isEditingForm', !!this.anyChange)
    }
  },
  mounted () {
    try {
      this.$store.dispatch('setHeaderForm', this.form)
      if (this.transactionType && this.transactionType.name === 'pc') {
        this.getSpecialPcLines(this.lines)
      }
      // Vue 3: the $root -> $children[0] -> $children[0] chain is no longer a reliable path to CoreLayout.
      // Find the ancestor/descendant that actually owns the appHeader + navSide refs instead.
      const findCoreLayout = (vm) => {
        if (!vm) return null
        if (vm.$refs && vm.$refs.appHeader && vm.$refs.navSide) return vm
        for (const child of (vm.$children || [])) {
          const found = findCoreLayout(child)
          if (found) return found
        }
        return null
      }
      const coreLayout = findCoreLayout(this.$root)
      if (!coreLayout) { this.focusOnFirstField(); return }
      const { appHeader, navSide } = coreLayout.$refs
      const appHeaderHeight = appHeader.$el.offsetHeight
      const trxTitleContainerHeight = this.$refs.trxTitleContainer.$el.offsetHeight
      const trxHeaderContainerHeight = this.$refs.trxHeaderContainer.$el.offsetHeight
      const distLinesHeight = coreLayout.$el.offsetHeight - (appHeaderHeight + trxTitleContainerHeight + trxHeaderContainerHeight)
      if (navSide.$el.offsetWidth && trxTitleContainerHeight && this.$refs.mockTrxTitleContainer) {
        this.$refs.trxTitleContainer.$el.style.width = `calc(100% - ${navSide.$el.offsetWidth}px)`
        this.$refs.mockTrxTitleContainer.$el.style.height = `${trxTitleContainerHeight}px`
      }
      if (distLinesHeight) {
        this.distLinesHeight = distLinesHeight
      }
      this.focusOnFirstField()
    } catch (e) {
      console.log(e)
    }
  },

  async created () {
    let resp = await this.$store.dispatch('fetchUiSetting', { name: 'DC_SETTINGS' })
    this.dcSettings = resp ? JSON.parse(resp.value) : {}

    let formStatuses = ['POSTED', 'CLOSED']
    if (this.transactionType.name === 'po') {
      formStatuses = ['CLOSED']
      this.unAllowedStatusesToChange = ['CLOSED', 'VOIDED']
    }
    // Check whether allowing edit of description, number in POSTED, CLOSED Statuses only
    if (formStatuses.includes(this.form.status)) {
      const allowed = await this.$store.dispatch('fetchUiSetting', { name: 'ALLOW_HEADER_CHANGES' })
      if (allowed && JSON.parse(allowed.value)) this.unAllowedStatusesToChange = ['VOIDED']
    }
  },

  beforeUnmount () {
    this.$store.dispatch('resetJeUpdateErrState')
    this.$store.dispatch('resetPcUpdateErrState')
    this.$store.dispatch('resetPoUpdateErrState')
    this.$store.dispatch('clearHeaderFormState')
  },
  beforeRouteLeave (to, from, next) {
    this.altRoute = this.generateAltRoute(to)
    const hasBeenChanged = Object.keys(this.headerDeltas).some(k => !this.excludedFields.includes(k)) ||
      !!Object.keys(this.lineDeltas).length
    const anyChange = hasBeenChanged || this.linesHaveBeenChanged
    if (hasBeenChanged && this.transactionIsBalanced) {
      this.handleDialogVisible(true)
    } else if (anyChange && !this.$store.getters.getRouteLeaveConfirmation) {
      return this.$store.dispatch('setDestinationRoute', to)
        .then(() => this.$store.dispatch('changeModalVisibility', { entityType: 'changeLossModal', visible: true }))
        .catch(e => console.log(e))
    } else {
      const p = [
        this.$store.dispatch('clearApTransactionLines', from.params.id),
        this.$store.dispatch('clearJeTransactionLines', from.params.id),
        this.$store.dispatch('clearPcTransactionLines', from.params.id),
        this.$store.dispatch('clearPoTransactionLines', from.params.id),
        this.$store.dispatch('isEditingForm', false)
      ]
      Promise.all(p)
        .then(() => next())
        .catch(e => {
          console.log(e)
          next()
        })
    }
    if (this.trxAlreadyExistsMsg) {
      this.trxAlreadyExistsMsg.close()
    }
  }
}
